import React, { useCallback } from 'react';

/**
 * ライセンスモーダルのプロパティ
 */
export interface LicenseModalProps {
  /** モーダルの表示状態 */
  visible: boolean;
  /** モーダルを閉じるコールバック */
  onClose: () => void;
  /** 右側表示モード（デフォルトはfalse） */
  rightSideMode?: boolean;
  /** メニューに戻るコールバック */
  onBackToMenu?: () => void;
}

/**
 * ライセンスモーダルコンポーネント
 *
 * 使用しているパッケージなどの権利表記を表示するモーダルウィンドウ
 */
export function LicenseModal({ visible, onClose, rightSideMode = false, onBackToMenu }: LicenseModalProps) {
  // 背景クリック時のハンドラ
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // 固定スタイル設定
  const containerBorderRadius = '16px';
  const headerPadding = '10px 15px';
  const headerHeight = '44px';
  const titleFontSize = '20px';
  const contentPadding = '16px';

  // モーダルが非表示の場合は何も表示しない
  if (!visible) {
    return null;
  }

  // 右側表示モードの場合のスタイル
  if (rightSideMode) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 2000,
          pointerEvents: 'auto' // マウスイベントを確実に捕捉
        }}
        onClick={handleBackdropClick}
      >
        <div
          style={{
            backgroundColor: 'white',
            height: 'calc(100% - 120px)',
            width: '400px',
            margin: '60px 90px 60px 0',
            borderRadius: containerBorderRadius,
            overflow: 'hidden', // overflowをhiddenに変更
            color: '#333',
            pointerEvents: 'auto', // モーダルコンテンツのマウスイベントを確実に捕捉
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
            opacity: 1
          }}
        >
          <div
            style={{
              padding: headerPadding,
              borderBottom: '1px solid #eee',
              height: headerHeight,
              position: 'relative'
            }}
          >
            <button
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                fontSize: '30px',
                cursor: 'pointer',
                padding: '0',
                zIndex: 2,
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4C4948'
              }}
              onClick={onBackToMenu || onClose}
            >
              ×
            </button>
            <h2
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                margin: 0,
                fontSize: titleFontSize,
                fontWeight: 'bold',
                textAlign: 'center',
                zIndex: 1
              }}
            >
              権利表記
            </h2>
          </div>

          <div
            style={{
              padding: contentPadding,
              overflowY: 'auto', // 縦方向のスクロールを有効化
              height: `calc(100% - ${headerHeight})` // コンテナの高さからヘッダー高さを引いた分
            }}
          >
            <LicenseContent />
          </div>
        </div>
      </div>
    );
  }

  // 通常のモーダル表示（中央）
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        pointerEvents: 'auto' // マウスイベントを確実に捕捉
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: containerBorderRadius,
          width: '80%',
          maxWidth: '800px',
          height: '80%',
          maxHeight: '600px',
          overflow: 'hidden', // overflowをhiddenに変更
          color: '#333',
          pointerEvents: 'auto', // モーダルコンテンツのマウスイベントを確実に捕捉
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          opacity: 1
        }}
      >
        <div
          style={{
            padding: headerPadding,
            borderBottom: '1px solid #eee',
            height: headerHeight,
            position: 'relative'
          }}
        >
          <h2
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              margin: 0,
              fontSize: titleFontSize,
              fontWeight: 'bold',
              textAlign: 'center',
              zIndex: 1
            }}
          >
            ライセンス情報
          </h2>
          <button
            style={{
              position: 'absolute',
              right: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0',
              zIndex: 2
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: contentPadding,
            overflowY: 'auto', // 縦方向のスクロールを有効化
            height: `calc(100% - ${headerHeight})` // コンテナの高さからヘッダー高さを引いた分
          }}
        >
          <LicenseContent />
        </div>
      </div>
    </div>
  );
}

/**
 * ライセンス情報のコンテンツコンポーネント
 */
function LicenseContent() {
  // 固定スタイル設定
  const linkFontSize = '14px';
  const preFontSize = '14px';

  // 共通スタイルの定義
  const titleStyle = {
    marginBottom: '12px',
    paddingLeft: '10px' // preの本文と左端を揃える
  };
  const linkStyle = {
    color: '#0366d6',
    textDecoration: 'none',
    fontSize: linkFontSize
  };
  const preStyle = {
    backgroundColor: 'rgba(255, 255, 255, 1)', // 完全不透明の白
    padding: '10px',
    borderRadius: '10px',
    fontSize: preFontSize,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    marginBottom: '20px'
  };

  return (
    <>
      <h4 style={titleStyle}>
        <a
          href='https://github.com/facebook/react/blob/main/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          React
        </a>
      </h4>
      <pre style={preStyle}>
        {`MIT License

Copyright (c) Meta Platforms, Inc. and affiliates.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/vercel/next.js/blob/canary/license.md'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          Next.js
        </a>
      </h4>
      <pre style={{ ...preStyle, padding: '16px' }}>
        {`The MIT License (MIT)

Copyright (c) 2025 Vercel, Inc.

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/mrdoob/three.js/blob/dev/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          Three.js
        </a>
      </h4>
      <pre style={preStyle}>
        {`The MIT License

Copyright © 2010-2025 three.js authors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/mkkellogg/GaussianSplats3D/blob/main/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          @mkkellogg/gaussian-splats-3d
        </a>
      </h4>
      <pre style={preStyle}>
        {`MIT License

Copyright (c) 2023 Mark Kellogg

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/pixiv/three-vrm/blob/dev/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          @pixiv/three-vrm
        </a>
      </h4>
      <pre style={preStyle}>
        {`Copyright (c) 2019-2025 pixiv Inc.

MIT License

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/pmndrs/react-three-fiber/blob/master/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          @react-three/drei & @react-three/fiber
        </a>
      </h4>
      <pre style={preStyle}>
        {`MIT License

Copyright (c) 2019-2025 Poimandres

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/hmans/miniplex/blob/main/LICENSE.md'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          miniplex & miniplex-react
        </a>
      </h4>
      <pre style={preStyle}>
        {`Copyright (c) 2023 Hendrik Mans

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/yoannmoinet/nipplejs/blob/master/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          nipplejs
        </a>
      </h4>
      <pre style={preStyle}>
        {`The MIT License (MIT)

Copyright (c) 2014 Yoann Moinet <yoann.moinet@gmail.com>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/yomotsu/camera-controls/blob/dev/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          camera-controls
        </a>
      </h4>
      <pre style={preStyle}>
        {`MIT License

Copyright (c) 2017 @yomotsu

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/donmccurdy/glTF-Transform/blob/main/LICENSE.md'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          @gltf-transform/core
        </a>
      </h4>
      <pre style={preStyle}>
        {`The MIT License (MIT)

Copyright (c) 2024 Don McCurdy

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/sparkjsdev/spark/blob/main/LICENSE'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          @sparkjsdev/spark
        </a>
      </h4>
      <pre style={preStyle}>
        {`MIT License

Copyright (c) 2025 WORLD LABS TECHNOLOGIES, INC.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>

      <h4 style={titleStyle}>
        <a
          href='https://github.com/remarkjs/react-markdown/blob/main/license'
          rel='noopener noreferrer'
          style={linkStyle}
          target='_blank'
        >
          react-markdown
        </a>
      </h4>
      <pre style={preStyle}>
        {`The MIT License (MIT)

Copyright (c) Espen Hovlandsdal

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
      </pre>
    </>
  );
}
